import type { ConfiguratorPageDefinition, StepHardcodedData } from "~/features/configurator/ConfiguratorPageDefinition";
import * as RailCstic from "~/features/configurator/Rail/v2/configuration/tkdm/TKDMRailsCstics";
import { ConfiguratorType } from "~/domains/configurator/types/ConfiguratorType";
import { TKDM_RAIL_CONFIGURATOR_PRODUCT_CODE } from "~/domains/configurator/constants";
import { BASE_DESIGNATION_SEPARATOR } from "~/features/configurator/Linear/constants";
import { type LinearConfiguration, type LinearCstic, type LinearSubstep } from "~/features/configurator/Linear/models";
import { getHighestVisitedStep } from "~/features/configurator/Linear/redux/selectors";
import { sendRailAddToCart } from "~/features/configurator/Rail/api/sendRailAddToCart";
import { sendRailUpdate } from "~/features/configurator/Rail/api/sendRailConfiguratorUpdate";
import { sendRailResetConfigurator } from "~/features/configurator/Rail/api/sendRailResetConfigurator";
import { sendRailStepChange } from "~/features/configurator/Rail/api/sendRailStepChange";
import { sendRailDataReset } from "~/features/configurator/Rail/api/sendRailStepReset";
import { getMainKmat } from "~/features/configurator/Shaft/common/commonShaftHelpers";
import type { RootState } from "~/app/store";
import { csticHasValue, getCsticsValue, join } from "~/features/configurator/common/v2/helper";
import { TKDMRailSummaryData } from "~/features/configurator/Rail/v2/configuration/tkdm/summary/TKDMRailSummaryData";
import { makeSendStepCompletedEvent, makeSummaryDataProvider } from "~/features/configurator/Shaft/shaftGaHelper";

import {
  getStep1SummaryData,
  getStep2SummaryData,
} from "~/features/configurator/Rail/v2/configuration/tkdm/summary/TKDMSummaryDataProvider";
import {
  prepareSubStepsInfo,
  transformSummaryDataToV2,
  transformToSelectionData,
} from "~/features/configurator/Shaft/SummaryDataProvider";
import { validateFromDefinition } from "~/features/configurator/common/v2/validation";
import { SubstepType } from "~/features/configurator/Linear/dtos";

const RAIL_CONFIGURATOR_STEP_DATA: StepHardcodedData[] = [
  {
    prepareData: (state, language: string) => {
      const summaryDataProvider = makeSummaryDataProvider(state, language);
      let step1Data = getStep1SummaryData(summaryDataProvider) ?? [];

      const substeps = [SubstepType.Series, SubstepType.Size];
      step1Data = [...prepareSubStepsInfo(summaryDataProvider, substeps)!, ...step1Data];

      return {
        name: summaryDataProvider.getGaStepName(1),
        gaName: summaryDataProvider.getGaStepName(1),
        tables: [
          {
            name: "",
            gaName: "",
            entries: step1Data.map(transformSummaryDataToV2),
          },
        ],
      };
    },
    trackCompletedStepAction: async (dispatch, getState) => {
      const state = getState();
      const { name, tables } = RAIL_CONFIGURATOR_STEP_DATA[0].prepareData(state, "en");
      const selectionData = transformToSelectionData(tables);

      sendStepCompletedEvent(name, selectionData);
    },
  },
  {
    prepareData: (state, language: string) => {
      const summaryDataProvider = makeSummaryDataProvider(state, language);
      const step2Data = getStep2SummaryData(summaryDataProvider) ?? [];

      return {
        name: summaryDataProvider.getGaStepName(2),
        gaName: summaryDataProvider.getGaStepName(2),
        tables: [
          {
            name: "",
            gaName: "",
            entries: step2Data.map(transformSummaryDataToV2),
          },
        ],
      };
    },
    trackCompletedStepAction: async (_, getState) => {
      const state = getState();
      const { name, tables } = RAIL_CONFIGURATOR_STEP_DATA[1].prepareData(state, "en");
      const selectionData = transformToSelectionData(tables);

      sendStepCompletedEvent(name, selectionData);
    },
  },
];

export const TKDM_RAIL_DEFINITION: ConfiguratorPageDefinition = {
  type: ConfiguratorType.Rail,
  productCode: TKDM_RAIL_CONFIGURATOR_PRODUCT_CODE,
  translationKeys: {
    pageTitle: "configurator.rail.name",
  },
  apiCalls: {
    update: sendRailUpdate,
    reset: sendRailResetConfigurator,
    stepChange: sendRailStepChange,
    stepReset: sendRailDataReset,
    addToCart: sendRailAddToCart,
  },
  summary: TKDMRailSummaryData,
  isAddToCartEnabled: (state: RootState) => {
    const step = getHighestVisitedStep(state);
    const kmat = getMainKmat({ kmats: state.linearConfigurator.kmats });
    const hasLengthSet = csticHasValue(kmat, RailCstic.GuidewayLength);
    return step >= 1 && hasLengthSet;
  },
  isSaveEnabled: (state: RootState) => {
    const step = getHighestVisitedStep(state);
    const kmat = getMainKmat({ kmats: state.linearConfigurator.kmats });
    const hasLengthSet = csticHasValue(kmat, RailCstic.GuidewayLength);

    return step >= 1 && hasLengthSet;
  },
  stepHardcodedData: RAIL_CONFIGURATOR_STEP_DATA,
  isConflictAvoidable: (csticName) => {
    return [RailCstic.GuidewayLength, RailCstic.AL, RailCstic.AR].includes(csticName);
  },
  generateDesignation: (kmats) => {
    const kmat = getMainKmat({ kmats });

    const [series, sizeCode, version, guidewayDesign] = getCsticsValue(
      kmat,
      RailCstic.DesignType_Series,
      RailCstic.DesignType_SizeCode,
      RailCstic.DesignType_Version,
      RailCstic.DesignType_GuidewayDesign,
    );

    return join("-", join("", series, sizeCode), version, guidewayDesign) || BASE_DESIGNATION_SEPARATOR;
  },
  gaConfiguratorName: "kuem-rail",
  modelAndUpdatePreprocessor: (model) => {
    const kmat = getMainKmat({ kmats: model.kmats });
    const cstics = kmat.cstics;

    const alCstic = cstics[RailCstic.AL];
    if (alCstic) {
      alCstic.label = "configurator.rail.section.al.name";
    }

    const arCstic = cstics[RailCstic.AR];
    if (arCstic) {
      arCstic.label = "configurator.rail.section.ar.name";
    }

    const lengthCstic = cstics[RailCstic.GuidewayLength];
    if (lengthCstic) {
      lengthCstic.label = "configurator.rail.cstic.guidewayLength.name";
    }

    cstics[RailCstic.PartSizing] = {
      ...EMPTY_FAKE_CSTIC,
      label: "configurator.rail.cstic.partSizing.name",
    };

    cstics[RailCstic.PartAmount] = {
      ...EMPTY_FAKE_CSTIC,
      label: "configurator.rail.cstic.partAmount.name",
      value: "1.0",
      values: [
        {
          key: "1.0",
          info: "",
          imageUrl: "",
          value: "1.0",
          label: "configurator.rail.cstic.partAmount.1.0.name",
        },
      ],
    };

    return model;
  },
  postDefinitionProcessor: (model, dto) => {
    model.allowedSteps = calculateAllowedSteps(model);
    return model;
  },
  canQueryAvailability: (state) => {
    const cstics = getMainKmat({ kmats: state.linearConfigurator.kmats }).cstics;

    const hasAllRequiredCstics = [RailCstic.GuidewayDesign, RailCstic.Size, RailCstic.Accuracy].every(
      (cstic) => !!cstics[cstic]?.value,
    );

    return hasAllRequiredCstics;
  },
} as const;

const EMPTY_FAKE_CSTIC: LinearCstic = {
  key: "",
  name: "",
  label: "",
  value: "",
  groupId: "",
  values: [],
  type: "",
  info: "",
  description: "",
  readonly: false,
  fake: true,
};

function calculateAllowedSteps(model: LinearConfiguration | LinearConfiguration): boolean[][] {
  const kmats = model.kmats;
  const conflicts = "conflictedCstics" in model ? model.conflictedCstics : [];

  const canVisitPage2 =
    model.steps[0].substeps?.every(
      (substep) => validateFromDefinition(model.substeps[substep] as LinearSubstep, kmats, conflicts).substepValid,
    ) ?? false;

  const canVisitPage3 =
    model.steps[1].substeps?.every(
      (substep) => validateFromDefinition(model.substeps[substep] as LinearSubstep, kmats, conflicts).substepValid,
    ) ?? false;

  const allowedSteps = [
    [true, canVisitPage2, canVisitPage3],
    [true, true, canVisitPage3],
    [true, true, true],
  ];

  return allowedSteps;
}

const sendStepCompletedEvent = makeSendStepCompletedEvent(TKDM_RAIL_DEFINITION);
